import IconDetail from "@/components/common/IconDetail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "@tanstack/react-router";
import Fuse from "fuse.js";
import {
  Check,
  Clock,
  Eye,
  FileQuestion,
  Globe,
  MessageCircleQuestion,
  Plus,
  Search,
  SortAsc,
  SortDesc,
  Tag,
  Target,
  Users,
} from "lucide-react";
import * as React from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";

interface FormOption {
  form_id: string;
  form_name: string;
  form_description?: string;
  prevalence?: string;
  scoring?: string;
  time_to_complete?: number;
  indications?: string[];
  orthopaedic_areas?: string[];
  number_of_pages?: number;
  number_of_questions?: number;
  is_published?: boolean;
}

interface FormSelectDialogProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  options: FormOption[];
  addOption?: React.ReactNode;
  label?: string;
  description?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
}

const prevalenceIcons: Record<string, React.ReactNode> = {
  ubiquitous: <Globe className="w-4 h-4" />,
  common: <Users className="w-4 h-4" />,
  uncommon: <Eye className="w-4 h-4" />,
  niche: <Target className="w-4 h-4" />,
};

export function FormSelectDialog<T extends FieldValues>({
  name,
  control,
  options,
  addOption,
  label,
  description,
  placeholder = "Select a form",
  disabled,
}: FormSelectDialogProps<T>) {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [alphaAsc, setAlphaAsc] = React.useState(true);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const navigate = useNavigate();

  // Fuzzy search setup
  const fuse = React.useMemo(
    () =>
      new Fuse(options, {
        keys: [
          "form_name",
          "form_description",
          "indications",
          "orthopaedic_areas",
        ],
        threshold: 0.4,
      }),
    [options],
  );

  let filtered = search ? fuse.search(search).map((r) => r.item) : options;
  filtered = [...filtered].sort((a, b) =>
    alphaAsc
      ? a.form_name.localeCompare(b.form_name)
      : b.form_name.localeCompare(a.form_name)
  );

  function handleSelect(form_id: string) {
    onChange(form_id);
    setOpen(false);
  }

  const selected = options.find((o) => o.form_id === value);

  const Content = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        {label && <label className="font-medium text-sm">{label}</label>}
        {description && (
          <span className="text-slate-500 text-xs">
            {description}
          </span>
        )}
      </div>
      <Button
        variant="outline"
        className={cn(
          "h-12 px-3 text-sm w-full justify-between rounded-md bg-white text-left",
          !selected && "text-slate-500",
        )}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <span className="text-sm truncate">
          {selected
            ? (
              <span className="font-medium">
                {selected.form_name}
              </span>
            )
            : placeholder}
        </span>
        <Search className="ml-2 w-4 h-4" />
      </Button>
      {error && <span className="text-red-500 text-xs">{error.message}</span>}
    </div>
  );

  const OptionCard = (option: FormOption) => (
    <button
      key={option.form_id}
      type="button"
      className={cn(
        "relative w-full text-left border rounded-lg p-4 flex flex-col gap-2 hover:bg-slate-50 dark:hover:bg-slate-900 transition",
        value === option.form_id &&
          "border-indigo-600 ring-2 ring-indigo-100 shadow-sm bg-indigo-50/40",
      )}
      onClick={() => handleSelect(option.form_id)}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "font-semibold text-base",
            value === option.form_id && "text-indigo-700",
          )}
        >
          {option.form_name}
        </span>
        {option.prevalence && (
          <span
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-md font-semibold text-[0.8rem] shadow-sm  border",
              option.prevalence === "ubiquitous"
                ? "bg-green-100 text-green-800 border-green-200"
                : option.prevalence === "common"
                ? "bg-blue-100 text-blue-800 border-blue-200"
                : option.prevalence === "uncommon"
                ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                : "bg-gray-100 text-gray-700 border-gray-200",
            )}
          >
            {prevalenceIcons[option.prevalence]}
            <span className="capitalize">
              {option.prevalence}
            </span>
          </span>
        )}
      </div>
      <div className="text-slate-500 text-xs">
        {option.form_description}
      </div>
      {/* Orthopaedic areas as badges and indications as text list */}
      {(option.orthopaedic_areas &&
          option.orthopaedic_areas.length > 0) ||
          (option.indications && option.indications.length > 0)
        ? (
          <>
            {option.orthopaedic_areas &&
              option.orthopaedic_areas.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {option.orthopaedic_areas.map((area) => (
                  <Badge key={area} color="cyan">
                    {area}
                  </Badge>
                ))}
              </div>
            )}
            {option.indications && option.indications.length > 0 &&
              (
                <div className="mt-1 text-slate-700 dark:text-slate-300 text-xs">
                  <span className="font-medium">
                    Indications:
                  </span>{" "}
                  {option.indications.join(", ")}
                </div>
              )}
          </>
        )
        : null}
      <div className="flex gap-4 mt-2 text-xs">
        {/* Time to complete as IconDetail */}
        {option.time_to_complete && (
          <IconDetail
            icon={<Clock size={18} />}
            words={`${option.time_to_complete} min`}
          />
        )}
        {option.number_of_pages !== undefined && (
          <IconDetail
            icon={<FileQuestion size={18} />}
            words={`${option.number_of_pages} pages`}
          />
        )}
        {option.number_of_questions !== undefined && (
          <IconDetail
            icon={<MessageCircleQuestion size={18} />}
            words={`${option.number_of_questions} questions`}
          />
        )}
      </div>
    </button>
  );

  const sortLabel = `Sort: ${alphaAsc ? "A-Z" : "Z-A"}`;
  const sortTooltip = `Sort alphabetically (${alphaAsc ? "A-Z" : "Z-A"})`;

  const List = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm"
          />
          <Search className="top-1/2 right-2 absolute w-4 h-4 text-slate-500 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setAlphaAsc((asc) => !asc)}
            title={sortTooltip}
            aria-label={sortTooltip}
          >
            {alphaAsc
              ? <SortAsc className="w-5 h-5" />
              : <SortDesc className="w-5 h-5" />}
          </Button>
          <span
            className="min-w-[80px] text-slate-500 text-xs"
            title={sortTooltip}
          >
            {sortLabel}
          </span>
        </div>
      </div>
      <ScrollArea className="h-[50vh]">
        <div className="flex flex-col gap-2">
          {filtered.length === 0 && (
            <div className="py-8 text-slate-400 text-center">
              No forms found.
            </div>
          )}
          {filtered.map(OptionCard)}
        </div>
      </ScrollArea>
      <div className="mt-2 pt-2 border-slate-200 border-t">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="flex items-center gap-2 w-full"
        >
          <Link to="/public/form/request" target="_blank">
            <Plus className="w-4 h-4" />
            Request New Form
          </Link>
        </Button>
      </div>
    </>
  );

  return (
    <div>
      {Content}
      {isMobile
        ? (
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>
                  Select a Questionnaire
                </DrawerTitle>
              </DrawerHeader>
              <div className="p-4">{List}</div>
            </DrawerContent>
          </Drawer>
        )
        : (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  Select a Questionnaire
                </DialogTitle>
              </DialogHeader>
              {List}
            </DialogContent>
          </Dialog>
        )}
    </div>
  );
}
